import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { BookOpen, Mail, Lock, User, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  const { user, loading: authLoading, rolesLoading, isStaff, signIn, signUp } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('auth');

  const loginSchema = z.object({
    email: z.string().trim().email(t('validation.invalidEmail')),
    password: z.string().min(6, t('validation.passwordMinLength')),
  });

  const signupSchema = loginSchema.extend({
    fullName: z.string().trim().min(2, t('validation.nameMinLength')).max(100, t('validation.nameTooLong')),
  });

  if (authLoading || (user && rolesLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex items-center gap-3">
          <BookOpen className="h-8 w-8 text-primary" />
          <span className="text-lg font-medium text-muted-foreground">{t('loading')}</span>
        </div>
      </div>
    );
  }

  if (user) {
    // Redirect staff/admin to staff portal, clients to client portal
    return <Navigate to={isStaff ? "/staff" : "/portal"} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      if (isLogin) {
        const result = loginSchema.safeParse({ email, password });
        if (!result.success) {
          const fieldErrors: Record<string, string> = {};
          result.error.errors.forEach(err => {
            fieldErrors[err.path[0]] = err.message;
          });
          setErrors(fieldErrors);
          setLoading(false);
          return;
        }

        const { error } = await signIn(email, password);
        if (error) {
          toast({
            title: t('toast.signInFailed'),
            description: error.message === 'Invalid login credentials'
              ? t('toast.invalidCredentials')
              : error.message,
            variant: 'destructive',
          });
        }
      } else {
        const result = signupSchema.safeParse({ email, password, fullName });
        if (!result.success) {
          const fieldErrors: Record<string, string> = {};
          result.error.errors.forEach(err => {
            fieldErrors[err.path[0]] = err.message;
          });
          setErrors(fieldErrors);
          setLoading(false);
          return;
        }

        const { error } = await signUp(email, password, fullName);
        if (error) {
          if (error.message.includes('already registered')) {
            toast({
              title: t('toast.accountExists'),
              description: t('toast.accountExistsDescription'),
              variant: 'destructive',
            });
          } else {
            toast({
              title: t('toast.signUpFailed'),
              description: error.message,
              variant: 'destructive',
            });
          }
        } else {
          setSignUpSuccess(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 primary-gradient items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="flex justify-center mb-8">
            <div className="h-20 w-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <BookOpen className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-white mb-4">
            {t('branding.appName')}
          </h1>
          <p className="text-xl text-white/90 mb-8">
            {t('branding.tagline')}
          </p>
          <div className="grid grid-cols-3 gap-4 text-white/80">
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur">
              <div className="text-2xl font-bold">3</div>
              <div className="text-sm">{t('stats.booksPerMonth')}</div>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur">
              <div className="text-2xl font-bold">{t('stats.age')}</div>
              <div className="text-sm">{t('stats.ageCurated')}</div>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur">
              <div className="text-2xl font-bold">∞</div>
              <div className="text-sm">{t('stats.adventures')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md shadow-elevated border-0">
          {signUpSuccess ? (
            <>
              <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-4">
                  <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <CardTitle className="text-2xl font-display">
                  {t('toast.welcomeTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {t('toast.checkEmailTitle')}
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    {t('toast.checkEmailDescription')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setSignUpSuccess(false);
                    setIsLogin(true);
                    setEmail('');
                    setPassword('');
                    setFullName('');
                  }}
                >
                  {t('toast.backToLogin')}
                </Button>
              </CardContent>
            </>
          ) : (
            <>
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4 lg:hidden">
              <div className="h-14 w-14 rounded-xl primary-gradient flex items-center justify-center">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-display">
              {isLogin ? t('login.title') : t('signup.title')}
            </CardTitle>
            <CardDescription>
              {isLogin
                ? t('login.description')
                : t('signup.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="fullName">{t('signup.fullNameLabel')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="fullName"
                      type="text"
                      placeholder={t('signup.fullNamePlaceholder')}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('login.emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('login.passwordLabel')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>

              {isLogin && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                    />
                    <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
                      {t('login.rememberMe')}
                    </Label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  t('login.loadingButton')
                ) : (
                  <>
                    {isLogin ? t('login.submitButton') : t('signup.submitButton')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setErrors({});
                }}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {isLogin ? t('login.noAccount') : t('signup.hasAccount')}
                <span className="font-medium text-primary">
                  {isLogin ? t('login.signUpLink') : t('signup.signInLink')}
                </span>
              </button>
            </div>
          </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
